// src/pages/CreateProduct/productFileManager.js
import * as XLSX from "xlsx";
import Papa from "papaparse";

/**
 * Delete a product from the parsed data by ID (SKU or name fallback)
 * @param {Object} parsedData - The result from parseProductsFile { rows, columns, columnLabels }
 * @param {string} productId - The SKU or name of the product to delete
 * @returns {Object} Updated parsedData with the product removed
 */
export function deleteProductFromFile(parsedData, productId) {
  if (!parsedData || !parsedData.rows) {
    throw new Error("Invalid parsed data provided");
  }

  const normalizeId = (id) => (id || "").toString().trim().toLowerCase();
  const targetId = normalizeId(productId);

  if (!targetId) {
    throw new Error("Product ID cannot be empty");
  }

  // Filter out the product that matches the ID
  const updatedRows = parsedData.rows.filter(row => {
    const sku = normalizeId(row.sku);
    const name = normalizeId(row.name_en);
    
    // Match by SKU first, then by name
    return !(sku === targetId || (!sku && name === targetId));
  });

  return {
    ...parsedData,
    rows: updatedRows
  };
}

/**
 * Update a product's fields in the parsed data
 * @param {Object} parsedData - The result from parseProductsFile { rows, columns, columnLabels }
 * @param {string} productId - The SKU or name of the product to update
 * @param {Object} updates - Object containing field updates
 * @returns {Object} Updated parsedData with the product modified
 */
export function updateProductInFile(parsedData, productId, updates) {
  if (!parsedData || !parsedData.rows) {
    throw new Error("Invalid parsed data provided");
  }

  if (!updates || typeof updates !== 'object') {
    throw new Error("Updates must be an object");
  }

  const normalizeId = (id) => (id || "").toString().trim().toLowerCase();
  const targetId = normalizeId(productId);

  if (!targetId) {
    throw new Error("Product ID cannot be empty");
  }

  let productFound = false;
  
  // Update the matching product
  const updatedRows = parsedData.rows.map(row => {
    const sku = normalizeId(row.sku);
    const name = normalizeId(row.name_en);
    
    // Match by SKU first, then by name
    if (sku === targetId || (!sku && name === targetId)) {
      productFound = true;
      
      // Process the updates with validation
      const processedUpdates = processProductUpdates(updates);
      
      return {
        ...row,
        ...processedUpdates
      };
    }
    
    return row;
  });

  if (!productFound) {
    throw new Error(`Product with ID "${productId}" not found`);
  }

  return {
    ...parsedData,
    rows: updatedRows
  };
}

/**
 * Process and validate product updates
 * @param {Object} updates - Raw updates object
 * @returns {Object} Processed updates with proper data types
 */
function processProductUpdates(updates) {
  const processed = { ...updates };
  
  // Convert price to number
  if ('price' in processed && processed.price !== null && updates.price !== undefined) {
    if (processed.price === '') {
      processed.price = 0;
    } else {
      const price = parseFloat(processed.price.toString().replace(/[^\d.-]/g, ''));
      processed.price = isNaN(price) ? 0 : price;
    }
  }
  
  // Convert stock to integer
  if ('stock' in processed && processed.stock !== null && updates.stock !== undefined) {
    if (processed.stock === '') {
      processed.stock = 0;
    } else {
      const stock = parseInt(processed.stock.toString().replace(/[^\d]/g, ''), 10);
      processed.stock = isNaN(stock) ? 0 : stock;
    }
  }
  
  // Convert min_order to integer
  if ('min_order' in processed && processed.min_order !== null && updates.min_order !== undefined) {
    if (processed.min_order === '') {
      processed.min_order = 1;
    } else {
      const minOrder = parseInt(processed.min_order.toString().replace(/[^\d]/g, ''), 10);
      processed.min_order = isNaN(minOrder) ? 1 : minOrder;
    }
  }
//   
  // Process shipping_period
  if ('shipping_period' in processed && processed.shipping_period !== null && updates.shipping_period !== undefined) {
    if (processed.shipping_period === '') {
      processed.shipping_period = 1;
    } else {
      const shippingPeriod = parseInt(processed.shipping_period.toString().replace(/[^\d]/g, ''), 10);
      processed.shipping_period = isNaN(shippingPeriod) ? 1 : shippingPeriod;
    }
  }

  // Process images - handle comma-separated URLs
  if ('images' in processed && processed.images !== null && updates.images !== undefined) {
    if (typeof processed.images === 'string' && processed.images.trim()) {
      const imageUrls = processed.images.split(/,(?=https?:\/\/)/) // split only when a new URL starts
      .map(url => url.trim()).filter(Boolean);
      processed.images = imageUrls.length > 1 ? imageUrls.join(',') : processed.images;
    }
  }
  
  // Convert category_id to integer if numeric
  if ('category_id' in processed && processed.category_id !== null && updates.category_id !== undefined) {
    if (processed.category_id === '') {
      // Leave as empty string or set to null based on your backend requirements
      processed.category_id = '';
    } else {
      const catId = parseInt(processed.category_id.toString().replace(/[^\d]/g, ''), 10);
      if (!isNaN(catId)) {
        processed.category_id = catId;
      }
    }
  }
  
  return processed;
}

/**
 * Export parsed data back to a CSV file
 * @param {Object} parsedData - The parsed data { rows, columns, columnLabels }
 * @param {string} filename - Desired filename (optional)
 * @returns {File} CSV File object ready to send to backend
 */
export function exportToCSV(parsedData, filename = 'products.csv') {
  if (!parsedData || !parsedData.rows || !parsedData.columns) {
    throw new Error("Invalid parsed data for export");
  }

  const { rows, columns } = parsedData;
  
  // Convert data to CSV format using Papa Parse
  const csv = Papa.unparse({
    fields: columns,
    data: rows.map(row => columns.map(col => row[col] || ''))
  });
  
  // Create a File object
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  return new File([blob], filename, { type: 'text/csv' });
}

/**
 * Export parsed data back to an XLSX file
 * @param {Object} parsedData - The parsed data { rows, columns, columnLabels }
 * @param {string} filename - Desired filename (optional)
 * @returns {File} XLSX File object ready to send to backend
 */
export function exportToXLSX(parsedData, filename = 'products.xlsx') {
  if (!parsedData || !parsedData.rows || !parsedData.columns) {
    throw new Error("Invalid parsed data for export");
  }

  const { rows, columns, columnLabels } = parsedData;
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Prepare data for worksheet (headers + rows)
  const headers = columns.map(col => columnLabels[col] || col);
  const wsData = [
    headers, // Header row
    ...rows.map(row => columns.map(col => row[col] || ''))
  ];
  
  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(wsData);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  
  // Write workbook to buffer
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  // Create a File object
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  return new File([blob], filename, { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
}

/**
 * Batch update multiple products
 * @param {Object} parsedData - The parsed data { rows, columns, columnLabels }
 * @param {Array} updates - Array of objects { productId, updates }
 * @returns {Object} Updated parsedData with all products modified
 */
export function batchUpdateProductsInFile(parsedData, updates) {
  if (!Array.isArray(updates)) {
    throw new Error("Updates must be an array");
  }

  let currentData = { ...parsedData };
  const errors = [];

  for (const update of updates) {
    try {
      if (!update.productId) {
        errors.push("Missing productId in update");
        continue;
      }
      
      currentData = updateProductInFile(currentData, update.productId, update.updates || {});
    } catch (error) {
      errors.push(`Error updating product ${update.productId}: ${error.message}`);
    }
  }

  if (errors.length > 0) {
    console.warn("Batch update completed with errors:", errors);
  }

  return {
    data: currentData,
    errors
  };
}

/**
 * Find products by various criteria
 * @param {Object} parsedData - The parsed data { rows, columns, columnLabels }
 * @param {Object} criteria - Search criteria object
 * @returns {Array} Array of matching products
 */
export function findProductsInFile(parsedData, criteria = {}) {
  if (!parsedData || !parsedData.rows) {
    return [];
  }

  const { rows } = parsedData;
  
  return rows.filter(row => {
    return Object.entries(criteria).every(([field, value]) => {
      if (!value && value !== 0) return true; // Skip empty criteria
      
      const rowValue = (row[field] || '').toString().toLowerCase().trim();
      const searchValue = value.toString().toLowerCase().trim();
      
      return rowValue.includes(searchValue);
    });
  });
}

// Usage examples:
/*

// Delete a product
const updatedData = deleteProductFromFile(parsedData, 'PROD-001');

// Update a product
const updatedData2 = updateProductInFile(parsedData, 'PROD-001', {
  name: 'Updated Product Name',
  price: 29.99,
  stock: 100
});

// Batch updates
const batchResult = batchUpdateProductsInFile(parsedData, [
  { productId: 'PROD-001', updates: { price: 25.99 } },
  { productId: 'PROD-002', updates: { stock: 50, min_order: 2 } }
]);

// Export to file for backend
const csvFile = exportToCSV(updatedData, 'updated-products.csv');
const xlsxFile = exportToXLSX(updatedData, 'updated-products.xlsx');

// Send to backend
const formData = new FormData();
formData.append('file', csvFile);
// or
formData.append('file', xlsxFile);

*/